import { BaseComponent } from '../base-component.js';
import { api } from '../../client/api.js';
import { router } from '../../shared/router.js';
import { escapeHtml } from '../../shared/utils.js';
import componentStyles from './auth-form.css?inline';

type State = 'idle' | 'loading' | 'success' | 'invalid-token';

export class ResetPassword extends BaseComponent {
  private state: State = 'idle';
  private error = '';
  private token = '';

  protected styles(): string {
    return componentStyles;
  }

  protected template(): string {
    if (this.state === 'invalid-token') {
      return `
        <div class="auth-container">
          <header class="header">
            <div class="logo">Agentor</div>
          </header>
          <div class="auth-form">
            <ag-panel>
              <ag-panel-header><h2>Invalid Link</h2></ag-panel-header>
              <ag-panel-content>
                <ag-alert class="danger">
                  This reset link is invalid or has expired. Please request a new one.
                </ag-alert>
                <div class="mode-switcher">
                  <a href="/forgot-password">Request New Link</a>
                </div>
              </ag-panel-content>
            </ag-panel>
          </div>
        </div>
      `;
    }

    if (this.state === 'success') {
      return `
        <div class="auth-container">
          <header class="header">
            <div class="logo">Agentor</div>
          </header>
          <div class="auth-form">
            <ag-panel>
              <ag-panel-header><h2>Password Reset</h2></ag-panel-header>
              <ag-panel-content>
                <ag-alert class="success">
                  Your password has been reset successfully. You can now sign in.
                </ag-alert>
                <div class="mode-switcher">
                  <a href="/login">Sign In</a>
                </div>
              </ag-panel-content>
            </ag-panel>
          </div>
        </div>
      `;
    }

    return `
      <div class="auth-container">
        <header class="header">
          <div class="logo">Agentor</div>
        </header>
        <div class="auth-form">
          <ag-panel>
            <ag-panel-header>
              <h2>Reset Password</h2>
            </ag-panel-header>
            <ag-panel-content>
              ${this.error ? `<div class="error">${escapeHtml(this.error)}</div>` : ''}
              <form id="form">
                <ag-form-group>
                  <label for="password">New Password</label>
                  <input type="password" id="password" name="password" required minlength="8" autocomplete="new-password">
                </ag-form-group>
                <ag-form-group>
                  <label for="confirm">Confirm Password</label>
                  <input type="password" id="confirm" name="confirm" required minlength="8" autocomplete="new-password">
                </ag-form-group>
                <button type="submit" ${this.state === 'loading' ? 'disabled' : ''}>
                  ${this.state === 'loading' ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
              <div class="mode-switcher">
                <a href="/login">Back to Sign In</a>
              </div>
            </ag-panel-content>
          </ag-panel>
        </div>
      </div>
    `;
  }

  connectedCallback(): void {
    super.connectedCallback();

    const params = new URLSearchParams(window.location.search);
    this.token = params.get('token') ?? '';

    if (!this.token) {
      this.state = 'invalid-token';
      this.render();
      return;
    }
  }

  protected afterRender(): void {
    this.$('#form')?.addEventListener('submit', (e) => this.handleSubmit(e));
  }

  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();

    const password = (this.$('#password') as HTMLInputElement).value;
    const confirm = (this.$('#confirm') as HTMLInputElement).value;

    if (password !== confirm) {
      this.error = 'Passwords do not match';
      this.render();
      this.afterRender();
      return;
    }

    this.state = 'loading';
    this.error = '';
    this.render();
    this.afterRender();

    const result = await api.resetPassword(this.token, password);

    if (result.success) {
      this.state = 'success';
      this.render();
      setTimeout(() => router.navigate('/login'), 2000);
    } else {
      this.state = 'idle';
      this.error = result.error || 'Reset failed. The link may have expired.';
      this.render();
      this.afterRender();
    }
  }
}

customElements.define('reset-password', ResetPassword);
